'use client'

import { motion } from 'framer-motion'
import { usePostHog } from 'posthog-js/react'
import { useCallback, useEffect, useState } from 'react'

import { ARENA_ENTRIES } from './arenaData'
import { ArenaResultModal } from './ArenaResultModal'
import { ArenaReviewPanel } from './ArenaReviewPanel'
import type { ArenaResult, ArenaSource } from './types'

function randomIndex(exclude?: number) {
  if (ARENA_ENTRIES.length <= 1) return 0
  let idx = Math.floor(Math.random() * ARENA_ENTRIES.length)
  if (exclude !== undefined && idx === exclude) {
    idx = (idx + 1) % ARENA_ENTRIES.length
  }
  return idx
}

export function ArenaContainer() {
  // Deterministic initial state so SSR and first client render match.
  // Randomize on mount to avoid a hydration mismatch from Math.random().
  const [currentIndex, setCurrentIndex] = useState(0)
  const posthog = usePostHog()
  const [humanIsSideA, setHumanIsSideA] = useState(false)
  const [revealed, setRevealed] = useState(false)
  const [chosenSide, setChosenSide] = useState<'A' | 'B' | null>(null)

  useEffect(() => {
    setCurrentIndex(randomIndex())
    setHumanIsSideA(Math.random() < 0.5)
  }, [])

  const uploadHref = '/upload'

  const entry = ARENA_ENTRIES[currentIndex]
  const sideASource: ArenaSource = humanIsSideA ? 'human' : 'ai'
  const sideBSource: ArenaSource = humanIsSideA ? 'ai' : 'human'
  const commentA = { content: humanIsSideA ? entry.humanComment : entry.aiComment }
  const commentB = { content: humanIsSideA ? entry.aiComment : entry.humanComment }

  const result: ArenaResult | null =
    revealed && chosenSide
      ? {
          sideASource,
          sideBSource,
          wasCorrect:
            (chosenSide === 'A' && sideASource === 'human') || (chosenSide === 'B' && sideBSource === 'human'),
        }
      : null

  const handleVote = (side: 'A' | 'B') => {
    if (revealed) return
    setChosenSide(side)
    setRevealed(true)

    // Autocapture already logs these clicks — ~11,000 of them since Feb 2026 —
    // but it can only see the button label, "A is Human". Which side held the
    // human comment is decided by Math.random() above and never leaves the
    // browser, so the autocaptured events carry no correctness signal at all
    // and cannot answer the one question the Arena exists to ask.
    //
    // `was_correct` is the whole point of this event. Without it the page can
    // report how many times it was played, which is a traffic number, not
    // evidence.
    posthog?.capture('arena_guess', {
      paper_id: entry.id,
      chosen_side: side,
      was_correct: (side === 'A') === humanIsSideA,
    })
  }

  const handleNextPaper = useCallback(() => {
    setCurrentIndex((prev) => randomIndex(prev))
    setHumanIsSideA(Math.random() < 0.5)
    setRevealed(false)
    setChosenSide(null)
  }, [])

  return (
    <>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}>
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold mb-2">Review Arena</h1>
          {/* Count comes from ARENA_ENTRIES so it cannot drift from the data. */}
          <p className="text-lg text-muted-foreground">
            The Review Arena presents {ARENA_ENTRIES.length} blinded pairs of peer review comments across computer
            science, life science, and social science venues. Each pair contains one comment written by a human reviewer
            and one written by Reviewer3 on the same manuscript, with the labels hidden. Can you guess which is which?
          </p>
        </div>

        {/* Paper title */}
        <p className="text-sm text-muted-foreground mb-4">
          <span className="font-medium text-foreground">{entry.venue}</span>: {entry.paperTitle}
        </p>

        {/* Comments side-by-side on desktop, stacked on mobile */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <ArenaReviewPanel
            side="A"
            comment={commentA}
            revealed={revealed}
            revealedSource={result?.sideASource}
            isChosen={chosenSide === 'A'}
            onVote={() => handleVote('A')}
            disabled={revealed}
          />
          <ArenaReviewPanel
            side="B"
            comment={commentB}
            revealed={revealed}
            revealedSource={result?.sideBSource}
            isChosen={chosenSide === 'B'}
            onVote={() => handleVote('B')}
            disabled={revealed}
          />
        </div>
      </motion.div>

      <ArenaResultModal revealed={revealed} result={result} onNextPaper={handleNextPaper} uploadHref={uploadHref} />
    </>
  )
}
